import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { exportPain001 } from '../../api/payments'
import Button from '../common/Button'
import Input from '../common/Input'
import { useUIStore } from '../../stores/uiStore'

/**
 * PaymentExportModal Component
 * Modal for collecting organization bank details and exporting PAIN.001 file
 */
export default function PaymentExportModal({ paymentIds, payments, onClose, onSuccess }) {
  const { showToast } = useUIStore()
  
  const [formData, setFormData] = useState({
    organization_name: '',
    organization_iban: '',
    organization_bic: '',
    organization_address: '',
    execution_date: new Date().toISOString().split('T')[0]
  })
  
  const [markAsPaid, setMarkAsPaid] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [errors, setErrors] = useState({})
  
  // Calculate totals
  const totalAmount = payments.reduce((sum, p) => sum + parseFloat(p.amount), 0)
  const paymentsWithoutIban = payments.filter(p => !p.trainer_iban)
  
  // Export mutation
  const exportMutation = useMutation({
    mutationFn: async (data) => {
      const response = await exportPain001(data)
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/xml' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition']
      let filename = 'PAIN001_export.xml'
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      return response
    },
    onSuccess: () => {
      showToast(`PAIN.001 file exported successfully${markAsPaid ? ' and payments marked as paid' : ''}`, 'success')
      onSuccess()
    },
    onError: (error) => {
      const message = error.response?.data?.error || 'Failed to export PAIN.001 file'
      showToast(message, 'error')
      
      // Show detailed errors if available
      if (error.response?.data?.details) {
        console.error('Export errors:', error.response.data.details)
      }
    }
  })
  
  // Validate IBAN format (basic)
  const validateIban = (iban) => {
    const cleaned = iban.replace(/\s/g, '')
    return /^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/i.test(cleaned)
  }
  
  // Validate BIC format (basic)
  const validateBic = (bic) => {
    return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/i.test(bic)
  }
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validate
    const newErrors = {}
    
    if (!formData.organization_name.trim()) {
      newErrors.organization_name = 'Organization name is required'
    }
    
    if (!formData.organization_iban.trim()) {
      newErrors.organization_iban = 'IBAN is required'
    } else if (!validateIban(formData.organization_iban)) {
      newErrors.organization_iban = 'Invalid IBAN format'
    }
    
    if (!formData.organization_bic.trim()) {
      newErrors.organization_bic = 'BIC is required'
    } else if (!validateBic(formData.organization_bic)) {
      newErrors.organization_bic = 'Invalid BIC format'
    }
    
    if (!formData.execution_date) {
      newErrors.execution_date = 'Execution date is required'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    
    setErrors({})
    setShowConfirmation(true)
  }
  
  // Handle confirmed export
  const handleConfirmedExport = () => {
    exportMutation.mutate({
      debtor: {
        name: formData.organization_name,
        iban: formData.organization_iban,
        bic: formData.organization_bic,
        address: formData.organization_address || undefined
      },
      payment_ids: paymentIds,
      mark_as_paid: markAsPaid,
      execution_date: formData.execution_date
    })
  }
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }
  
  if (showConfirmation) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Confirm Export</h2>
          </div>
          
          <div className="px-6 py-4 space-y-4">
            <p className="text-sm text-gray-600">
              You are about to export <strong>{paymentIds.length}</strong> payment(s) 
              totaling <strong>{formatCurrency(totalAmount)}</strong> to a PAIN.001 file.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Organization Details</h3>
              <dl className="text-sm space-y-1">
                <div>
                  <dt className="inline font-medium text-blue-900">Name: </dt>
                  <dd className="inline text-blue-700">{formData.organization_name}</dd>
                </div>
                <div>
                  <dt className="inline font-medium text-blue-900">IBAN: </dt>
                  <dd className="inline text-blue-700">{formData.organization_iban}</dd>
                </div>
                <div>
                  <dt className="inline font-medium text-blue-900">BIC: </dt>
                  <dd className="inline text-blue-700">{formData.organization_bic}</dd>
                </div>
                <div>
                  <dt className="inline font-medium text-blue-900">Execution Date: </dt>
                  <dd className="inline text-blue-700">
                    {new Date(formData.execution_date).toLocaleDateString('nl-NL')}
                  </dd>
                </div>
              </dl>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={markAsPaid}
                  onChange={(e) => setMarkAsPaid(e.target.checked)}
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <div>
                  <p className="text-sm font-medium text-yellow-900">
                    Mark exported payments as paid
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    This will update the payment status to "paid" for all exported payments. 
                    This action cannot be undone automatically.
                  </p>
                </div>
              </label>
            </div>
          </div>
          
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() => setShowConfirmation(false)}
              disabled={exportMutation.isPending}
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmedExport}
              disabled={exportMutation.isPending}
            >
              {exportMutation.isPending ? 'Exporting...' : 'Confirm & Export'}
            </Button>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-semibold text-gray-900">Export PAIN.001 File</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Export Summary</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Total Payments</p>
                <p className="font-semibold text-gray-900">{paymentIds.length}</p>
              </div>
              <div>
                <p className="text-gray-600">Total Amount</p>
                <p className="font-semibold text-gray-900">{formatCurrency(totalAmount)}</p>
              </div>
            </div>
            
            {paymentsWithoutIban.length > 0 && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> {paymentsWithoutIban.length} payment(s) are missing IBAN information 
                  and cannot be exported. Please add IBAN information to the trainers first.
                </p>
              </div>
            )}
          </div>
          
          {/* Organization Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900">Organization Bank Details</h3>
            
            <Input
              label="Organization Name *"
              value={formData.organization_name}
              onChange={(e) => setFormData(prev => ({ ...prev, organization_name: e.target.value }))}
              error={errors.organization_name}
              placeholder="VCV Sports Organization"
            />
            
            <Input
              label="IBAN *"
              value={formData.organization_iban}
              onChange={(e) => setFormData(prev => ({ ...prev, organization_iban: e.target.value.toUpperCase() }))}
              error={errors.organization_iban}
              placeholder="NL00 BANK 0123 4567 89"
              helperText="Your organization's IBAN for debiting payments"
            />
            
            <Input
              label="BIC/SWIFT Code *"
              value={formData.organization_bic}
              onChange={(e) => setFormData(prev => ({ ...prev, organization_bic: e.target.value.toUpperCase() }))}
              error={errors.organization_bic}
              placeholder="ABNANL2A"
              helperText="Your bank's BIC/SWIFT code"
            />
            
            <Input
              label="Organization Address (Optional)"
              value={formData.organization_address}
              onChange={(e) => setFormData(prev => ({ ...prev, organization_address: e.target.value }))}
              placeholder="Street 123, 1234 AB City"
            />
            
            <Input
              label="Execution Date *"
              type="date"
              value={formData.execution_date}
              onChange={(e) => setFormData(prev => ({ ...prev, execution_date: e.target.value }))}
              error={errors.execution_date}
              helperText="The date when payments should be processed"
            />
          </div>
          
          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={paymentsWithoutIban.length > 0}
            >
              Continue
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}